import { updateDocumentNode, updateDesignationOnDocument } from "common/meeting/util";

export function removeManyAnnotationsFromCache(query, cacheProxy, { annotationIds, meetingId }) {
  const ids = new Set(annotationIds);
  const { meeting, ...otherData } = cacheProxy.readQuery({
    query,
    variables: { meetingId },
  });
  const associatedDesignationIds = [];
  const meetingWithFilteredAnnotations = updateDocumentNode(
    meeting,
    (docNode) => docNode.annotations.edges.some((edge) => ids.has(edge.node.id)),
    (docNode) => ({
      ...docNode,
      annotations: {
        ...docNode.annotations,
        edges: docNode.annotations.edges.filter((edge) => {
          if (ids.has(edge.node.id)) {
            associatedDesignationIds.push(edge.node.annotationDesignationId);
            return false;
          }
          return true;
        }),
      },
    }),
  );
  const finalNewMeeting = associatedDesignationIds.reduce((accumMeeting, designationId) => {
    return designationId
      ? updateDesignationOnDocument(accumMeeting, designationId, (designation) => ({
          ...designation,
          fulfilled: false,
        }))
      : accumMeeting;
  }, meetingWithFilteredAnnotations);
  cacheProxy.writeQuery({
    query,
    data: { meeting: finalNewMeeting, ...otherData },
    variables: { meetingId },
  });
}

/**
 * @description
 * This function will add a new annotation to the Apollo cache for a particular document.
 * This assumes that query is a MeetingQuery, querying both for meeting and viewer.
 */
export function removeAnnotationFromCache(query, cacheProxy, { annotationId, meetingId, errors }) {
  if (errors?.length) {
    return;
  }
  removeManyAnnotationsFromCache(query, cacheProxy, { meetingId, annotationIds: [annotationId] });
}

/**
 * @description
 * This function will add a new annotation to the Apollo cache for a particular document.
 */
export function addNewAnnotationToDocumentCache(
  query,
  cacheProxy,
  /** @type any */
  { annotationDesignationId, meetingId, documentId, newAnnotation, errors },
) {
  if (errors?.length) {
    return;
  }
  const { meeting, ...otherData } = cacheProxy.readQuery({
    query,
    variables: { meetingId },
  });

  const newMeeting = updateDocumentNode(
    meeting,
    (docNode) =>
      docNode.id === documentId &&
      // Its possible that this annotation is already in the graph cache (due to a socket event racing with
      // a mutation completion, for instance). This check/protection makes the operation idempotent.
      !docNode.annotations.edges.find((edge) => edge.node.id === newAnnotation.id),
    (docNode) => ({
      ...docNode,
      annotations: {
        ...docNode.annotations,
        edges: [
          ...docNode.annotations.edges,
          {
            node: newAnnotation,
            __typename: "AnnotationEdge",
          },
        ],
      },
    }),
  );

  const finalNewMeeting = annotationDesignationId
    ? updateDesignationOnDocument(newMeeting, annotationDesignationId, (designation) => ({
        ...designation,
        fulfilled: true,
      }))
    : newMeeting;

  cacheProxy.writeQuery({
    query,
    data: { meeting: finalNewMeeting, ...otherData },
    variables: { meetingId },
  });
}

/**
 * @description
 * This function will either add a new radio checkmark annotation to the Apollo cache for a particular document
 * or re-associate existing annotation with a another designation.
 */
export function addRadioAnnotationToDocumentCache(
  query,
  cacheProxy,
  /** @type any */
  { annotationDesignationId, meetingId, documentId, newAnnotation, errors },
) {
  if (errors?.length) {
    return;
  }
  const { meeting, ...otherData } = cacheProxy.readQuery({
    query,
    variables: { meetingId },
  });
  const {
    documentBundle: { documents },
  } = meeting;
  const documentNode = documents.edges.find((edge) => edge.node.id === documentId).node;
  const targetDesignation = documentNode.annotationDesignations.edges.find(
    (edge) => edge.node.id === annotationDesignationId,
  ).node;
  const previousDesignation = documentNode.annotationDesignations.edges.find(
    (edge) =>
      edge.node.fulfilled &&
      edge.node.designationGroupId === targetDesignation.designationGroupId &&
      edge.node.id !== annotationDesignationId,
  )?.node;
  // Its possible that graph cache update already happened
  // Ex. objects in cache get directly updated if mutation payload has objects with matching id
  // This check/protection makes the operation idempotent.
  if (previousDesignation && !previousDesignation.fulfilled) {
    return;
  }
  const previousAnnotation =
    previousDesignation &&
    documentNode.annotations.edges.find(
      (edge) => edge.node.annotationDesignationId === previousDesignation.id,
    )?.node;
  // With radio-annotations we don't create new annotation if one already exists (in a group)
  // Instead we move existing annotation to a new location and re-assing it to another designation
  const finalAnnotation = previousAnnotation
    ? { ...newAnnotation, id: previousAnnotation.id }
    : newAnnotation;
  const newDesignationEdges = documentNode.annotationDesignations.edges.map((edge) =>
    edge.node.designationGroupId !== targetDesignation.designationGroupId
      ? edge
      : edge.node.id === targetDesignation.id
      ? {
          ...edge,
          node: {
            ...edge.node,
            fulfilled: true,
            required: false,
          },
        }
      : edge.node.id === previousDesignation?.id
      ? {
          ...edge,
          node: {
            ...edge.node,
            fulfilled: false,
            required: false,
          },
        }
      : {
          ...edge,
          node: {
            ...edge.node,
            required: false,
          },
        },
  );
  const newAnnotationEdges = [
    ...documentNode.annotations.edges.filter((edge) => edge.node.id !== finalAnnotation.id),
    {
      node: finalAnnotation,
      __typename: "AnnotationEdge",
    },
  ];
  const newMeeting = {
    ...meeting,
    documentBundle: {
      ...meeting.documentBundle,
      documents: {
        ...documents,
        edges: documents.edges.map((edge) =>
          edge.node.id === documentId
            ? {
                ...edge,
                node: {
                  ...edge.node,
                  annotations: {
                    ...edge.node.annotations,
                    edges: newAnnotationEdges,
                  },
                  annotationDesignations: {
                    ...edge.node.annotationDesignations,
                    edges: newDesignationEdges,
                  },
                },
              }
            : edge,
        ),
      },
    },
  };

  cacheProxy.writeQuery({
    query,
    data: { meeting: newMeeting, ...otherData },
    variables: { meetingId },
  });
}
